import { computed, ref } from 'vue'
import type { FlowHistoryEntry, FlowSnapshot } from '../model/types'
import type { FlowDocument } from './useFlowDocument'

/**
 * Undo/redo as a list of full graph snapshots, plus the saved-state marker used by the
 * "Unsaved changes" label. `restore` puts a snapshot back on the canvas.
 */
export function useFlowHistory(document: FlowDocument, restore: (snap: FlowSnapshot) => void) {
  const history = ref<FlowHistoryEntry[]>([])
  const historyPointer = ref(0)
  const canUndo = ref(false)
  const canRedo = ref(false)
  const lastSavedSnapshot = ref<FlowSnapshot | null>(null)
  const lastSavedAt = ref<number | null>(null)

  let historyCounter = 0
  const nextHistoryId = () => `h-${++historyCounter}`

  const orderedHistoryEntries = computed(() =>
    history.value.map((entry, index) => ({ entry, index })).reverse(),
  )

  const refreshFlags = () => {
    canUndo.value = historyPointer.value > 0
    canRedo.value = historyPointer.value < history.value.length - 1
  }

  /** Records the current graph, discarding any redo entries. */
  const pushHistory = (label: string) => {
    const snap = document.snapshot()
    if (historyPointer.value < history.value.length - 1) {
      history.value = history.value.slice(0, historyPointer.value + 1)
    }
    history.value.push({ id: nextHistoryId(), label, timestamp: Date.now(), snapshot: snap })
    historyPointer.value = history.value.length - 1
    refreshFlags()
  }

  /** Runs `mutate` and records a history entry only if the graph actually changed. */
  const runMutation = (label: string, mutate: () => void) => {
    const before = JSON.stringify(document.snapshot())
    mutate()
    if (before !== JSON.stringify(document.snapshot())) pushHistory(label)
  }

  /** Starts a fresh history with the current graph as its only entry. */
  const resetHistory = (label: string) => {
    history.value = [
      { id: nextHistoryId(), label, timestamp: Date.now(), snapshot: document.snapshot() },
    ]
    historyPointer.value = 0
    refreshFlags()
  }

  const goTo = (index: number) => {
    const entry = history.value[index]
    if (!entry) return
    historyPointer.value = index
    restore(entry.snapshot)
    refreshFlags()
  }

  const undo = () => {
    if (!canUndo.value) return
    historyPointer.value -= 1
    const entry = history.value[historyPointer.value]
    if (entry) restore(entry.snapshot)
    refreshFlags()
  }

  const redo = () => {
    if (!canRedo.value) return
    historyPointer.value += 1
    const entry = history.value[historyPointer.value]
    if (entry) restore(entry.snapshot)
    refreshFlags()
  }

  const markSaved = () => {
    lastSavedSnapshot.value = document.snapshot()
    lastSavedAt.value = Date.now()
  }

  const isDirty = computed(() => {
    if (!lastSavedSnapshot.value) return true
    return JSON.stringify(document.snapshot()) !== JSON.stringify(lastSavedSnapshot.value)
  })

  const saveStatusLabel = computed(() => {
    if (isDirty.value) return 'Unsaved changes'
    if (!lastSavedAt.value) return null
    return `Saved at ${new Date(lastSavedAt.value).toLocaleTimeString()}`
  })

  return {
    history,
    historyPointer,
    orderedHistoryEntries,
    canUndo,
    canRedo,
    lastSavedSnapshot,
    isDirty,
    saveStatusLabel,
    pushHistory,
    runMutation,
    resetHistory,
    goTo,
    undo,
    redo,
    markSaved,
  }
}

export type FlowHistory = ReturnType<typeof useFlowHistory>
